import { useState } from "react";
import Card from "components/Card/Card.jsx";
import { Modal } from "react-bootstrap";
import Button from "components/CustomButton/CustomButton";
import WaitlistSettingsHour from "components/Settings/WaitlistSettingsHour";
import "./SpecificDateForm.scss";
import { collections } from "services";
import { db } from "firebase.js";
import { DateTime } from "luxon";

function SpecificDateForm(props) {
  const {
    onHide,
    showDateForm,
    date,
    selectedEvent,
    business,
    resources = [],
    existingEvents = [],
    // Set when opened from a single resource's own hours editor: the scope is fixed
    // to that resource and the "Applies to" picker never appears, so there is no way
    // to accidentally create or edit a business-wide or other-resource override.
    lockedResourceId = null,
    lockedResourceName = ""
  } = props;

  const [form, setForm] = useState({
    closed: selectedEvent?.event?.closed || false,
    time: {
      begin: selectedEvent?.event?.begin || "00:00",
      end: selectedEvent?.event?.end || "11:45"
    }
  });

  // "" means the override applies to the whole business. Scope is fixed once a doc
  // exists — re-scoping is Remove then re-add, which avoids having to delete the
  // field and avoids colliding with an override that already covers the target.
  const [resourceId, setResourceId] = useState(selectedEvent?.event?.resourceId || lockedResourceId || "");
  // WaitlistSettingsHour keeps internal state that never re-syncs to its props, so
  // reloading the form for a different scope has to remount it.
  const [hoursKey, setHoursKey] = useState(0);

  // The override already covering the chosen scope on this date, if any. Editing an
  // event pins it; otherwise it is whatever the scope dropdown currently points at.
  const existing = selectedEvent || existingEvents.find(e => (e.event.resourceId || "") === resourceId);

  const onScopeChange = event => {
    const nextResourceId = event.target.value;
    setResourceId(nextResourceId);

    // Load whatever is already set for that scope so Save updates it rather than
    // silently overwriting it with the defaults.
    const match = existingEvents.find(e => (e.event.resourceId || "") === nextResourceId);
    setForm({
      closed: match?.event?.closed || false,
      time: { begin: match?.event?.begin || "00:00", end: match?.event?.end || "11:45" }
    });
    setHoursKey(key => key + 1);
  };

  const onValidChange = (day, values) => {
    let f = form;
    if (values.begin) {
      f.time.begin = values.begin;
    }
    if (values.end) {
      f.time.end = values.end;
    }
    if (values.closed !== undefined) {
      f.closed = values.closed;
    }
    setForm({ ...f });
  };

  const scopeName = lockedResourceId
    ? lockedResourceName
    : resourceId
      ? resources.find(r => r.id === resourceId)?.name
      : "";

  const formatDate = date => {
    if (date) {
      let dt = DateTime.fromJSDate(date, { zone: business.settings?.timezone }).startOf("day");
      return dt.toLocaleString(DateTime.DATE_SHORT);
    }
  };

  const onSave = async () => {
    // Day-click only ever resolves the business-wide override, so a date that
    // already carries one for the chosen scope must be updated, not duplicated.
    if (existing) {
      let ref = db
        .collection(collections.businesses)
        .doc(business.businessId)
        .collection(collections.shifts)
        .doc(existing.event.id);

      // `resourceId` is deliberately untouched — scope is immutable after creation.
      await ref.update({ closed: form.closed, begin: form.time.begin, end: form.time.end });
    } else {
      let dt = DateTime.fromJSDate(date, { zone: business.settings?.timezone }).startOf("day");
      let ref = db.collection(collections.businesses).doc(business.businessId).collection(collections.shifts).doc();
      const payload = {
        date: dt.toJSDate(),
        closed: form.closed,
        begin: form.time.begin,
        end: form.time.end,
        accountId: business.accountId
      };
      // Omitted rather than nulled when business-wide, so legacy docs and new
      // business-wide docs are indistinguishable to every reader.
      if (resourceId) {
        payload.resourceId = resourceId;
      }
      await ref.set(payload);
    }
    onHide();
  };

  const onRemove = async () => {
    await db
      .collection(collections.businesses)
      .doc(business.businessId)
      .collection(collections.shifts)
      .doc(existing.event.id)
      .delete();
    onHide();
  };

  return (
    <Modal className="specificDateForm-modal" show={showDateForm} bsSize="lg" onHide={onHide}>
      <Card
        title={`Hours for ${formatDate(date)}${scopeName ? ` — ${scopeName}` : ""}`}
        content={
          // Plain divs rather than Row/Col: the card content is already inset, and a
          // Row's negative margins would pull each field back out to the edge.
          <div className="specific-date-form">
            {lockedResourceId ? (
              <div className="specific-date-form__hint">
                This override only applies to {lockedResourceName || "this resource"}.
              </div>
            ) : (
              resources.length > 0 && (
                <>
                  <div className="specific-date-form__row">
                    <div className="specific-date-form__label">Applies to</div>
                    <select
                      className="form-control specific-date-form__select"
                      value={resourceId}
                      disabled={!!selectedEvent}
                      onChange={onScopeChange}
                    >
                      <option value="">All resources</option>
                      {resources.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="specific-date-form__hint">
                    {selectedEvent
                      ? "Remove this override and add it again to change who it applies to."
                      : "Applies to the whole business unless you pick a single resource."}
                  </div>
                </>
              )
            )}
            <WaitlistSettingsHour
              key={hoursKey}
              value={!form.closed}
              disabled={false}
              time={form.time}
              onValidChange={onValidChange}
              day={"Set hours"}
            />
            <div className="specific-date-form__hint">
              <strong>Note:</strong> Changing booking hours will not affect existing reservations on this date.
            </div>
            <div className="specific-date-form__actions">
              {existing && (
                <Button onClick={onRemove} bsStyle="danger" type="button">
                  Remove
                </Button>
              )}
              <Button onClick={onHide} bsStyle="info" type="button">
                Cancel
              </Button>
              <Button onClick={onSave} bsStyle="info" fill type="button">
                Save
              </Button>
            </div>
          </div>
        }
      ></Card>
    </Modal>
  );
}

export default SpecificDateForm;
